import {useSelector} from 'react-redux';

import {portMonitorAPI} from 'features/port/api/portMonitorAPI';
import {
    getPortMonitorsFilterArray,
    PORT_MONITORS_DEFAULT_SORT,
    PORT_MONITORS_ORDER_FIELDS,
} from 'features/port/lib/portFilters';
import {useMonitorList, IMonitorListState} from 'entities/monitor';
import {useCreateGate} from 'entities/tariff';
import {IUserOutput, userSelector, useHasTariffLimit} from 'entities/user';
import IOrderBy from 'shared/model/IOrderBy';
import IPagination from 'shared/api/model/IPagination';
import useListParams from 'shared/lib/hooks/useListParams';
import {getPollingInterval} from 'shared/api/pollingHelper';

import {IPortMonitorListItem} from './PortMonitor/IPortMonitor';
import {
    clearAll,
    isSelectedAllSelector,
    portMonitorSelectedIdsSelector,
    selectAllOnPage,
} from './portMonitorSlice';
import useCanCreatePortMonitor from './useCanCreatePortMonitor';

export interface IUsePortMonitors {
    user: IUserOutput | null,
    activeCount: number | undefined,
    sort: IOrderBy | undefined,
    pagination: IPagination<IPortMonitorListItem> | undefined,
    isLoading: boolean,
    isFetching: boolean,
    refetch: () => void,
    list: IMonitorListState,
    handleCreate: () => void,
    isCreateOpen: boolean,
    isExceededOpen: boolean,
    closeCreate: () => void,
}

export const usePortMonitors = (): IUsePortMonitors => {
    const {args, sort, filters, hasFilters} = useListParams({
        getFilters: getPortMonitorsFilterArray,
        defaultSort: PORT_MONITORS_DEFAULT_SORT,
        sortFields: PORT_MONITORS_ORDER_FIELDS,
    });
    const create = useCreateGate(useCanCreatePortMonitor());

    const user = useSelector(userSelector);

    const hasAccess = useHasTariffLimit('port_monitor_max_count_limit');

    const {data: pagination, isLoading, isFetching, refetch} =
        portMonitorAPI.useGetPortMonitorsQuery(args, {
            skip: !hasAccess,
            pollingInterval: getPollingInterval(),
        });

    const [getAllPortMonitorIds] = portMonitorAPI.useLazyGetAllPortMonitorIdsQuery();

    const {data: summary} = portMonitorAPI.useGetSummaryPortMonitorsQuery(undefined, {
        skip: !hasAccess,
        pollingInterval: getPollingInterval(),
    });

    const list = useMonitorList({
        pagination,
        isLoading,
        tariffField: 'port_monitor_max_count_limit',
        hasFilters,
        selection: {
            selectedIdsSelector: portMonitorSelectedIdsSelector,
            isSelectedAllSelector,
            selectAllOnPage,
            clearAll,
        },
        getAllIds: async() => (await getAllPortMonitorIds({sort, filters})).data,
    });

    return {
        user,
        activeCount: summary?.total_active,
        sort,
        pagination,
        isLoading,
        isFetching,
        refetch,
        list,
        handleCreate: create.open,
        isCreateOpen: create.isCreateOpen,
        isExceededOpen: create.isExceededOpen,
        closeCreate: create.close,
    };
};

export default usePortMonitors;
